export type Ring = number[][]
export type Feature = {
  id?: string | number
  properties?: Record<string, unknown>
  geometry?: { type: string; coordinates: unknown } | null
}

const WRAP = 180

function unwrap(ring: Ring): Ring {
  if (ring.length === 0) return ring
  const out: Ring = [[...ring[0]!]]
  let offset = 0

  for (let i = 1; i < ring.length; i++) {
    const d = ring[i]![0]! - ring[i - 1]![0]!
    if (d > WRAP) offset -= 360
    else if (d < -WRAP) offset += 360
    out.push([ring[i]![0]! + offset, ring[i]![1]!])
  }
  return out
}

function clipToStrip(ring: Ring, left: number, right: number): Ring {
  const half = (poly: Ring, keep: (p: number[]) => boolean, at: number): Ring => {
    const out: Ring = []
    for (let i = 0; i < poly.length; i++) {
      const cur = poly[i]!
      const prev = poly[(i - 1 + poly.length) % poly.length]!
      const curIn = keep(cur)
      const prevIn = keep(prev)

      if (curIn !== prevIn) {
        const t = (at - prev[0]!) / (cur[0]! - prev[0]!)
        out.push([at, prev[1]! + t * (cur[1]! - prev[1]!)])
      }
      if (curIn) out.push(cur)
    }
    return out
  }

  const a = half(ring, (p) => p[0]! >= left, left)
  if (a.length === 0) return []
  return half(a, (p) => p[0]! <= right, right)
}

export function splitRing(ring: Ring): Ring[] {
  if (ring.length < 3) return []

  const open = unwrap(ring)
  const lons = open.map((p) => p[0]!)
  const min = Math.min(...lons)
  const max = Math.max(...lons)
  if (min >= -180 && max <= 180) return [ring]

  const from = Math.floor((min + 180) / 360)
  const to = Math.floor((max + 180) / 360)
  const out: Ring[] = []

  for (let k = from; k <= to; k++) {
    const piece = clipToStrip(open, -180 + 360 * k, 180 + 360 * k)
    if (piece.length >= 3) out.push(piece.map(([lon, lat]) => [lon! - 360 * k, lat!]))
  }
  return out
}

export function splitAntimeridian<T extends Feature>(features: readonly T[]): T[] {
  return features.map((f) => {
    const g = f.geometry
    if (!g) return f

    const polygons: Ring[][] =
      g.type === "Polygon"
        ? [g.coordinates as Ring[]]
        : g.type === "MultiPolygon"
          ? (g.coordinates as Ring[][])
          : []
    if (polygons.length === 0) return f

    const out: Ring[][] = []
    for (const poly of polygons) {
      const [outer, ...holes] = poly
      if (!outer) continue
      for (const piece of splitRing(outer)) {
        // Holes are clipped into the same strip as their outer ring. Holes in country outlines (enclaves,
        // lakes) are small and never cross the antimeridian themselves, so the outer ring's strip is enough
        out.push([piece, ...holes.flatMap((h) => splitRing(h))])
      }
    }

    if (out.length === 0) return f
    return { ...f, geometry: { type: "MultiPolygon", coordinates: out } }
  })
}
